<template>
  <div class="role-list">
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增角色按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'role:edit'">新增角色</el-button>
        <!-- 批量删除按钮（选中角色后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'role:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('role:edit') || selectedIds.length === 0">批量删除</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="角色名称">
            <el-input v-model="searchForm.roleName" placeholder="请输入角色名称" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="角色编码">
            <el-input v-model="searchForm.roleCode" placeholder="请输入角色编码" maxlength="30"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button icon="el-icon-search" type="primary" @click="loadRoles">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="roles" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="roleName" label="角色名称" header-align="center"></el-table-column>
      <el-table-column prop="roleCode" label="角色编码" header-align="center"></el-table-column>
      <el-table-column prop="description" label="角色描述" header-align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="权限数量" align="center">
        <template slot-scope="scope">
          {{ scope.row.permissions ? scope.row.permissions.length : 0 }}
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'role:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="分配权限" placement="top">
            <el-button icon="el-icon-key" type="warning" circle v-hasPermission="'role:edit'"
              @click="openAssignPermissionsDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'role:edit'"
              @click="handleDelete(scope.row.id)" :disabled="scope.row.roleCode === 'ROLE_ADMIN'"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="form.roleName" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="角色编码" prop="roleCode">
          <el-input v-model="form.roleCode" placeholder="以ROLE_开头，如ROLE_ADMIN" :disabled="isEdit"
            maxlength="30"></el-input>
        </el-form-item>
        <el-form-item label="角色描述" prop="description">
          <el-input type="textarea" v-model="form.description" rows="3" maxlength="200"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配权限对话框 -->
    <el-dialog title="分配权限" :visible.sync="assignPermissionsVisible" width="600px" max-height="600px">
      <el-tree :data="permissionTree" show-checkbox node-key="id" :default-checked-keys="selectedPermissionIds"
        :props="treeProps" ref="permissionTree" class="permission-tree"></el-tree>
      <div slot="footer">
        <el-button @click="assignPermissionsVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="savePermissions">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import store from '../../store';
import roleApi from '../../api/role'
import permissionApi from '../../api/permission'
import { filterSuperAdminRole } from '../../utils/roleFilter'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'RoleList',
  data() {
    // 角色编码唯一校验
    const checkRoleCodeUnique = async (rule, value, callback) => {
      if (!value) {
        // 空值不校验（由required规则处理）
        return callback()
      }

      try {
        const params = {
          roleCode: value,
          excludeId: this.isEdit ? this.recordId : null
        }
        // 调用后端查重接口
        const response = await roleApi.checkRoleCode(params)
        if (response.success && response.exists) {
          // 角色编码已存在，返回错误提示
          callback(new Error('该角色编码已被使用，请更换'))
        } else {
          // 角色编码可用，验证通过
          callback()
        }
      } catch (error) {
        callback(new Error('检查失败，请稍后重试'))
      }
    }

    return {
      // 表格数据
      roles: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,

      // 搜索表单
      searchForm: {
        roleName: '',
        roleCode: ''
      },

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增角色',
      isEdit: false,
      recordId: null, // 编辑时记录当前角色的ID，用于排除自身
      form: {
        id: null,
        roleName: '',
        roleCode: '',
        description: ''
      },
      rules: {
        roleName: [
          { required: true, message: '请输入角色名称', trigger: 'blur' },
          { min: 1, max: 20, message: '长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        roleCode: [
          { required: true, message: '请输入角色编码', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_]+$/, message: '角色编码只能包含字母、数字和下划线', trigger: 'blur' },
          { pattern: /^ROLE_.+/, message: '角色编码必须以ROLE_开头', trigger: 'blur' },
          { min: 6, max: 30, message: '长度在 6 到 30 个字符', trigger: 'blur' },
          { validator: checkRoleCodeUnique, trigger: 'blur' }
        ]
      },

      // 分配权限
      assignPermissionsVisible: false,
      selectedRoleId: null,
      currentUserId: store.state.user.userInfo.id,
      permissionTree: [],
      selectedPermissionIds: [],
      treeProps: {
        children: 'children',
        label: 'permissionName'
      }
    }
  },
  created() {
    this.loadRoles()
  },

  computed: {
    // 计算属性：过滤后的角色列表
    filteredRoles() {
      // 调用工具函数：非超级管理员（ID≠1）则过滤掉角色ID=1的超级管理员
      return filterSuperAdminRole(this.allRoles, this.currentUserId);
    },
  },

  methods: {
    // 加载角色列表
    loadRoles() {
      // 组装请求参数
      const params = {
        roleName: this.searchForm.roleName, // 搜索-角色名称
        roleCode: this.searchForm.roleCode, // 搜索-角色编码
        page: this.currentPage,           // 分页-当前页
        size: this.pageSize               // 分页-每页条数
      };
      roleApi.findAll(params).then(response => {
        if (response.success) {
          this.roles = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载角色失败：' + error.message)
      })
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        roleName: '',
        roleCode: ''
      }
      this.loadRoles()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadRoles(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadRoles(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('role:edit')) {
        this.$message.error('无新增角色权限');
        return;
      }
      this.dialogTitle = '新增角色'
      this.isEdit = false
      this.recordId = null
      this.form = {
        id: null,
        roleName: '',
        roleCode: 'ROLE_',
        description: ''
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('role:edit')) {
        this.$message.error('无编辑角色权限');
        return;
      }
      this.dialogTitle = '编辑角色'
      this.isEdit = true
      this.recordId = row.id
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新角色
            roleApi.update(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadRoles()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增角色
            roleApi.add(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadRoles()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除角色
    handleDelete(id) {
      if (!hasPermission('role:edit')) {
        this.$message.error('无删除角色权限');
        return;
      }
      this.$confirm('确定要删除该角色吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        roleApi.delete(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadRoles()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除用户
    handleBatchDelete() {
      if (!hasPermission('role:edit')) {
        this.$message.error('无删除角色权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的角色');
        return;
      }
      this.$confirm('确定要批量删除选中的角色吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        roleApi.deleteBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadRoles()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 打开分配权限对话框
    openAssignPermissionsDialog(row) {
      if (!hasPermission('role:edit')) {
        this.$message.error('无分配权限权限');
        return;
      }
      this.selectedRoleId = row.id
      this.assignPermissionsVisible = true

      // 加载所有权限
      permissionApi.findAll().then(response => {
        if (response.success) {
          // 构建权限树形结构
          this.permissionTree = this.buildPermissionTree(response.data.data)

          // 获取已选权限ID
          this.selectedPermissionIds = row.permissions ?
            row.permissions.map(permission => permission.id) : []
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载权限失败：' + error.message)
      })
    },

    // 构建权限树形结构
    buildPermissionTree(permissions) {
      const tree = []
      const map = {}

      // 先将所有权限放入map
      permissions.forEach(permission => {
        map[permission.id] = { ...permission, children: permission.children || [] }
      })

      // 构建树形结构
      permissions.forEach(permission => {
        if (permission.parentId === null || permission.parentId === 0) {
          // 根节点
          tree.push(map[permission.id])
        } else {
          // 子节点
          if (map[permission.parentId]) {
            map[permission.parentId].children.push(map[permission.id])
          }
        }
      })

      return tree
    },

    // 保存权限分配
    savePermissions() {
      // 获取所有选中的权限ID
      const checkedIds = this.$refs.permissionTree.getCheckedKeys()

      roleApi.assignPermissions(this.selectedRoleId, checkedIds).then(response => {
        if (response.success) {
          this.$message.success(response.message || '权限分配成功')
          this.assignPermissionsVisible = false
          this.loadRoles()
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('权限分配失败：' + error.message)
      })
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },
  }
}
</script>

<style scoped>
.permission-tree {
  max-height: 400px;
  overflow-y: auto;
}
</style>
